import { OK } from 'http-status-codes';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { isTokenExpired } from '../../../utils';
import { getFirebaseBridge } from '../firebaseBridge';
import * as tokensModule from '../tokens';
import { refreshAccessToken } from './api';
import { actions, types } from './duck';

export function* clearRefreshAccessToken() {
  const refreshToken = yield select(state => state.auth.tokens.refresh);

  // Firebase (customer) sessions have no backend refresh token: the
  // Firebase SDK keeps the session alive and hands back a fresh ID token.
  if (!refreshToken) {
    const { getIdToken } = getFirebaseBridge();
    const idToken = getIdToken ? yield call(getIdToken) : null;
    if (!idToken) {
      throw Error('Firebase session expired.');
    }
    return { refresh: null, access: idToken };
  }

  // 'exp' is a JWT token expiration timestamp in microsecconds.
  const refreshTokenExp = yield select(
    state => state.auth.tokens.refreshDecoded.exp,
  );
  const refreshTokenExpired = isTokenExpired(refreshTokenExp);

  if (refreshTokenExpired) {
    throw Error('Refresh token was expired.');
  }

  const response = yield refreshAccessToken(refreshToken);

  if (response.status !== OK) {
    throw Error(response.data.detail);
  }

  const newAccessToken = response.data.access;
  const updatedTokens = { refresh: refreshToken, access: newAccessToken };

  return updatedTokens;
}

export function* refreshAuthSaga() {
  // Firebase (customer) sessions have no refresh token; the web Firebase saga owns those.
  const hasRefreshToken = yield select(state => !!state.auth.tokens.refresh);
  if (!hasRefreshToken) return;

  try {
    yield put(actions.refreshAuthRequest());
    const refreshedTokens = yield clearRefreshAccessToken();
    yield put(tokensModule.actions.setTokens(refreshedTokens));
    yield put(actions.refreshAuthSuccess(refreshedTokens));
  } catch (error) {
    yield put(actions.refreshAuthFailure(error.message));
  }
}

export default function* saga() {
  yield takeLatest(types.REFRESH_AUTH, refreshAuthSaga);
}